import { Button } from "../../../../../core/Button.js";
import { cardStyles } from "../../../../../core/Card.styles.js";
import { Text } from "../../../../../core/Text.js";
import { BattleSeat } from "../../../../../domain/battles/BattleSeat.js";
import { ItemArt } from "../../../../../domain/ItemArt.js";
import { ActionButton } from "../../../../shared/ActionButton.js";
import type { BattleOutcomeContent } from "../../../battlesContent.js";

/**
 * How it ended, centered: the rule over the headline (the winner's name
 * opens their profile), what was pulled, a losing seat's prize, then the viewer's claim (claim or
 * sell back, never both) and the way back. A spectator gets the headline and the way back.
 */
export function BattleOutcome({
  eyebrow,
  headline,
  summary,
  prize,
  claim,
  error,
  replay,
  back,
}: BattleOutcomeContent) {
  return (
    <section className="flex flex-col items-center gap-6 py-4 text-center md:py-8">
      <div className="flex flex-col items-center gap-2">
        <Text variant="caption-eyebrow" tone="tertiary">
          {eyebrow}
        </Text>
        <Text as="h2" variant="display-xl2" tone="primary" className="max-md:text-display-xl3">
          {"winner" in headline ? (
            <>
              <BattleSeat seat={{ ...headline.winner, isWinner: false }} layout="name" />{" "}
              {headline.verb}
            </>
          ) : (
            headline.text
          )}
        </Text>
        <Text as="p" variant="body-b2" tone="secondary">
          {summary}
        </Text>
      </div>
      {prize && (
        <figure
          className={cardStyles({
            radius: "xl",
            className: "flex items-center gap-3 px-3 py-2 text-start",
          })}
        >
          <ItemArt src={prize.imageUrl} alt="" className="w-10 shrink-0" />
          <figcaption className="flex flex-col">
            <Text variant="body-b2-strong" tone="primary">
              {prize.name}
            </Text>
            <Text variant="body-b3" tone="tertiary">
              {prize.note}
            </Text>
          </figcaption>
        </figure>
      )}
      {claim?.status === "done" && (
        <Text as="p" variant="body-b2-strong" tone="success" role="status">
          {claim.message}
        </Text>
      )}
      {claim?.status === "open" && (
        <div className="flex flex-col items-center gap-3">
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              variant="brand"
              size="lg"
              disabled={claim.disabled}
              loading={claim.claim.loading}
              onClick={claim.claim.onClaim}
            >
              {claim.claim.label}
            </Button>
            {claim.sellBack && (
              <Button
                variant="secondary"
                size="lg"
                disabled={claim.disabled}
                loading={claim.sellBack.loading}
                onClick={claim.sellBack.onSellBack}
              >
                {claim.sellBack.label}
              </Button>
            )}
          </div>
          <Text as="p" variant="body-b3" tone="tertiary" className="max-w-md">
            {claim.note}
          </Text>
        </div>
      )}
      {error && (
        <Text as="p" variant="body-b2" tone="danger" role="alert">
          {error}
        </Text>
      )}
      <div className="flex flex-wrap justify-center gap-3">
        <Button variant="secondary" size="sm" onClick={replay.onReplay}>
          {replay.label}
        </Button>
        <ActionButton action={{ label: back.label, href: back.href }} variant="ghost" size="sm" />
      </div>
    </section>
  );
}
